import { AUTH_ACTIONS } from '../store/reducers/auth';

interface AuthConfig {
  authority: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  postLogoutRedirectUri?: string;
}

interface OpenIDConfiguration {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
  end_session_endpoint: string;
  revocation_endpoint: string;
  jwks_uri: string;
  response_types_supported: string[];
  subject_types_supported: string[];
  id_token_signing_alg_values_supported: string[];
  scopes_supported: string[];
  token_endpoint_auth_methods_supported: string[];
  claims_supported: string[];
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  id_token?: string;
}

export interface User {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_at: number;
  scope: string;
  profile?: any;
}

export class PKCEAuth {
  private static readonly authConfig: AuthConfig = {
    authority: process.env.REACT_APP_AUTHENTICATION_URL?.replace(/\/$/, '') || '', // Remove trailing slash if present
    clientId: process.env.REACT_APP_AUTHENTICATION_KEY || '',
    redirectUri: process.env.REACT_APP_AUTHENTICATION_CALLBACK_URL || window.location.origin + '/callback',
    scope: `openid ${process.env.REACT_APP_APPLICATION_NAME || ''} ${process.env.REACT_APP_RESOURCE_APPLICATION_SCOPE_NAME || ''}`,
    postLogoutRedirectUri: process.env.REACT_APP_LOGOUT_CALLBACK_URL,
  };

  private static openIdConfig: OpenIDConfiguration | null = null;

  private static async getOpenIdConfig(): Promise<OpenIDConfiguration> {
    if (this.openIdConfig) return this.openIdConfig;

    const response = await fetch(`${this.authConfig.authority}/.well-known/openid-configuration`);
    if (!response.ok) {
      throw new Error('Failed to fetch OpenID Configuration');
    }

    const config: OpenIDConfiguration = await response.json();
    this.openIdConfig = config;
    return config;
  }

  private static generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private static async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(hash)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }

  private static readonly storageKeys = {
    codeVerifier: 'pkce_code_verifier',
    state: 'pkce_state',
    nonce: 'pkce_nonce',
    user: 'pkce_user'
  };

  static async initiateLogin(): Promise<void> {
    try {
      const config = await this.getOpenIdConfig();
      const state = this.generateRandomString(32);
      const codeVerifier = this.generateRandomString(64);
      const codeChallenge = await this.generateCodeChallenge(codeVerifier);
      const nonce = this.generateRandomString(32);

      // Store PKCE and state parameters
      sessionStorage.setItem(this.storageKeys.codeVerifier, codeVerifier);
      sessionStorage.setItem(this.storageKeys.state, state);
      sessionStorage.setItem(this.storageKeys.nonce, nonce);

      const params = new URLSearchParams({
        client_id: this.authConfig.clientId,
        response_type: 'code',
        scope: this.authConfig.scope,
        redirect_uri: this.authConfig.redirectUri,
        state: state,
        nonce: nonce,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
      });

      window.location.href = `${config.authorization_endpoint}?${params.toString()}`;
    } catch (error) {
      console.error('Login initiation failed:', error);
      throw error;
    }
  }

  static saveCurrentPage = (): void => {
    // Only save path if not already on an authentication-related page
    const currentPath = window.location.pathname + window.location.search;
    const authPaths = ['/login', '/callback', '/error'];
    if (!authPaths.some(path => currentPath.includes(path))) {
      localStorage.setItem('LoadedUrl', currentPath);
    }
  }
  
  static async handleCallback(): Promise<User> {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const storedState = sessionStorage.getItem(this.storageKeys.state);
      const codeVerifier = sessionStorage.getItem(this.storageKeys.codeVerifier);

      if (!code || !state || !storedState || !codeVerifier) {
        throw new Error('Invalid callback parameters');
      }

      if (state !== storedState) {
        throw new Error('State mismatch');
      }

      // Exchange code for tokens
      const config = await this.getOpenIdConfig();
      const tokenResponse = await fetch(config.token_endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: this.authConfig.clientId,
          code_verifier: codeVerifier,
          code: code,
          redirect_uri: this.authConfig.redirectUri
        })
      });

      if (!tokenResponse.ok) {
        throw new Error('Token exchange failed');
      }

      const tokens = await tokenResponse.json();
      const user: User = {
        access_token: tokens.access_token,
        id_token: tokens.id_token,
        refresh_token: tokens.refresh_token,
        expires_at: Date.now() + (tokens.expires_in * 1000),
        scope: tokens.scope
      };

      // Clean up storage
      sessionStorage.removeItem(this.storageKeys.codeVerifier);
      sessionStorage.removeItem(this.storageKeys.state);
      sessionStorage.removeItem(this.storageKeys.nonce);

      // Store user data
      sessionStorage.setItem(this.storageKeys.user, JSON.stringify(user));

      return user;
    } catch (error) {
      console.error('Callback handling failed:', error);
      throw error;
    }
  }

  static async logout(idTokenHint?: string, dispatch?: Function): Promise<void> {
    try {
      const state = this.generateRandomString(32);
      sessionStorage.setItem(this.storageKeys.state, state);
      
      // Get base URL from environment or fallback to default
      const idPlatformUrl = (process.env.REACT_APP_AUTHENTICATION_URL?.replace(/\/$/, '') || 'https://id.trimble.com');
      
      // Construct logout URL exactly as specified by Trimble Identity Platform
      let logoutUrl = `${idPlatformUrl}/oauth/logout`;

      // Required parameters in specific order
      const params = new URLSearchParams();
      if (idTokenHint) {
        params.append('id_token_hint', idTokenHint);
      }
      params.append('post_logout_redirect_uri', this.authConfig.postLogoutRedirectUri || window.location.origin);
      params.append('state', state);

      // First clear Redux auth state if dispatch is provided 
      if (dispatch) {
        dispatch({ type: AUTH_ACTIONS.LOGOUT }); // Clear auth state in Redux
      }

      // Then clear local storage and session storage
      this.clearState();
      window.location.href = `${logoutUrl}?${params.toString()}`;
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }

  static async revokeTokens(): Promise<void> {
    try {
      const userStr = sessionStorage.getItem(this.storageKeys.user);
      if (!userStr) return;

      const user: User = JSON.parse(userStr);
      if (user.refresh_token) {
        const config = await this.getOpenIdConfig();
        await fetch(config.revocation_endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `bearer ${user.access_token}`
          },
          body: new URLSearchParams({
            client_id: this.authConfig.clientId,
            token: user.refresh_token,
            token_type_hint: 'refresh_token'
          })
        });
      }

      sessionStorage.removeItem(this.storageKeys.user);
    } catch (error) {
      console.error('Token revocation failed:', error);
      throw error;
    }
  }

  static clearState(): void {
    const loadedUrl = localStorage.getItem('LoadedUrl') || '/home'; 
    sessionStorage.removeItem(this.storageKeys.codeVerifier);
    sessionStorage.removeItem(this.storageKeys.state);
    sessionStorage.removeItem(this.storageKeys.nonce);
    sessionStorage.removeItem(this.storageKeys.user);
    sessionStorage.removeItem('state');
    // One-time cleanup of the legacy unscoped team keys that older builds wrote.
    // These are no longer read anywhere, but may still linger in a user's
    // browser from before this fix shipped.
    localStorage.removeItem('MY_TEAM_KEY');
    localStorage.removeItem('MY_SELECTED_TEAM_');
    localStorage.setItem('LoadedUrl', loadedUrl);
  }
}
